import { ReactElement, useEffect, useState } from 'react';
import { OnPageChangedType } from '@root/src/app/designSystem/pagination/types';
import {
  BoldSpan,
  ContainerWithMargin,
  NavigationIconContainer,
  PageInput,
  PaginationContainer,
} from './styles';
import {
  PaginationLeftIcon,
  PaginationRightIcon,
} from '@root/src/assets/images';
import Button from '@root/src/app/designSystem/buttons';
import Lang from '@lang/en';

export interface PaginationTypes {
  totalRecords: number;
  pageLimit: number;
  currentPage: number;
  'data-testid'?: string;
  onPageChanged: OnPageChangedType;
  debounceTimeout?: number;
}

const calculateTotalPages = (pageLimit: number, totalRecords: number) =>
  pageLimit ? Math.ceil(totalRecords / pageLimit) : 1;

const getValidatedPageNumber = (value: string, totalPages: number) => {
  if (!value || value === ' ') return 1;
  const numericValue = parseInt(value);
  if (numericValue >= totalPages) return totalPages;
  if (numericValue <= 0) return 1;
  return numericValue;
};

const defaultDebounceTimeout = 1000;

const Pagination = ({
  totalRecords,
  pageLimit,
  currentPage,
  onPageChanged,
  debounceTimeout = defaultDebounceTimeout,
  ...rest
}: PaginationTypes): ReactElement => {
  const [inputValue, setInputValue] = useState(currentPage);
  const [totalPages, setTotalPages] = useState(
    calculateTotalPages(pageLimit, totalRecords)
  );
  const [timeoutId, setTimeoutId] = useState<Timeout | null>(null);

  useEffect(() => {
    return (): void => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  useEffect(() => {
    setInputValue(currentPage);
  }, [currentPage]);

  const handleOnChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(getValidatedPageNumber(value, totalPages));
    const timeoutId = setTimeout(() => {
      onPageChanged(getValidatedPageNumber(value, totalPages));
    }, debounceTimeout);
    setTimeoutId(timeoutId);
  };

  useEffect(() => {
    setTotalPages(calculateTotalPages(pageLimit, totalRecords));
  }, [pageLimit, totalRecords]);

  const onPreviousPageClick = () => {
    const newCurrentPage = currentPage - 1;
    onPageChanged(newCurrentPage);
  };

  const onNextPageClick = () => {
    const newCurrentPage = currentPage + 1;
    onPageChanged(newCurrentPage);
  };

  const isFirstPage = () => {
    return currentPage <= 1;
  };

  const isLastPage = () => {
    return currentPage >= totalPages;
  };

  return (
    <PaginationContainer data-testid={rest['data-testid']}>
      <ContainerWithMargin>{Lang.paginationPageLabel}</ContainerWithMargin>
      {!isFirstPage() && (
        <Button
          aria-label={Lang.paginationPreviousPageAriaLabel}
          type="icon"
          icon={
            <NavigationIconContainer>
              <PaginationLeftIcon />
            </NavigationIconContainer>
          }
          onClick={onPreviousPageClick}
        />
      )}
      <PageInput type="number" onChange={handleOnChange} value={inputValue} />
      {!isLastPage() && (
        <Button
          aria-label={Lang.paginationNextPageAriaLabel}
          type="icon"
          icon={
            <NavigationIconContainer>
              <PaginationRightIcon />
            </NavigationIconContainer>
          }
          onClick={onNextPageClick}
        />
      )}
      <ContainerWithMargin>
        <span>
          {Lang.paginationOutOfLabel}
          <BoldSpan>{` ${totalPages}`}</BoldSpan>
        </span>
      </ContainerWithMargin>
    </PaginationContainer>
  );
};

export default Pagination;
